import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import toast from "../../../utils/toast";
import { apiService } from "../../../services/apiService";
import {
  ActivityReport,
  ActivityReportFilters,
} from "../../../types";

export const useManagerDashboardSharedData = ({
  mainTab,
  user,
  canViewCosts,
  t,
}: any) => {
  const [reports, setReports] = useState<any[]>([]);
  const [projects, setProjects] = useState<any[]>([]);
  const [projectCollections, setProjectCollections] = useState<any[]>([]);
  const [activityDefinitions, setActivityDefinitions] = useState<any[]>([]);
  const [reportsLoading, setReportsLoading] = useState(true);
  const [projectsLoading, setProjectsLoading] = useState(true);
  const [reportsLoaded, setReportsLoaded] = useState(false);
  const [users, setUsers] = useState<any[]>([]);
  const [userHourlyCostRates, setUserHourlyCostRates] = useState<any[]>([]);
  const [companyAverageHourlyCostRates, setCompanyAverageHourlyCostRates] =
    useState<any[]>([]);
  const tRef = useRef(t);

  useEffect(() => {
    tRef.current = t;
  }, [t]);

  const fetchUsers = useCallback(async () => {
    try {
      const response = await apiService.getUsers(true);
      setUsers(response);
    } catch (error) {
    }
  }, []);

  const fetchProjects = useCallback(async () => {
    try {
      setProjectsLoading(true);
      const projectsData = await apiService.getWorkflowInstances();
      setProjects(projectsData);
    } catch (error) {
      toast.error(tRef.current("errors:loadFailed"));
    } finally {
      setProjectsLoading(false);
    }
  }, []);

  const fetchProjectCollections = useCallback(
    async (workflowInstanceIds: number[]) => {
      if (!canViewCosts) {
        setProjectCollections([]);
        return;
      }

      const validIds = workflowInstanceIds.filter(
        (id) => Number.isFinite(id) && id > 0,
      );

      if (!validIds.length) {
        setProjectCollections([]);
        return;
      }

      try {
        const collections = await apiService.getProjectCollectionsForInstances(
          validIds,
        );
        setProjectCollections(Array.isArray(collections) ? collections : []);
      } catch (error) {
        setProjectCollections([]);
      }
    },
    [canViewCosts],
  );

  const fetchCostRates = useCallback(async () => {
    if (!canViewCosts) {
      setUserHourlyCostRates([]);
      setCompanyAverageHourlyCostRates([]);
      return;
    }

    try {
      const [userRatesResponse, averageRatesResponse] = await Promise.all([
        apiService.getHourlyCostRates(),
        apiService.getAverageHourlyCostRates(),
      ]);

      const userRates = (userRatesResponse?.data || userRatesResponse) as
        | any[]
        | null;
      const averageRates = (averageRatesResponse?.data ||
        averageRatesResponse) as any[] | null;

      setUserHourlyCostRates(Array.isArray(userRates) ? userRates : []);
      setCompanyAverageHourlyCostRates(
        Array.isArray(averageRates) ? averageRates : [],
      );
    } catch (error) {
      setUserHourlyCostRates([]);
      setCompanyAverageHourlyCostRates([]);
    }
  }, [canViewCosts]);

  const fetchActivities = useCallback(async () => {
    try {
      const companyId = (user?.company?.id ?? user?.companyId) as
        | number
        | undefined;
      if (!companyId) {
        return;
      }
      const activities = await apiService.getActivitiesByCompany(companyId);
      setActivityDefinitions(activities || []);
    } catch (error) {
    }
  }, [user]);

  const applyLegacyExactReportFilters = useCallback(
    (
      reportData: ActivityReport[],
      filters?: ActivityReportFilters,
    ): ActivityReport[] => {
      if (!filters) {
        return reportData;
      }

      return reportData.filter((report) => {
        if (
          typeof filters.workflowInstanceId === "number" &&
          report.workflowId !== filters.workflowInstanceId
        ) {
          return false;
        }

        if (
          filters.workflowName &&
          (report.workflowName || "").trim() !== filters.workflowName
        ) {
          return false;
        }

        if (
          filters.activityTitle &&
          (report.activityTitle || "").trim() !== filters.activityTitle
        ) {
          return false;
        }

        return true;
      });
    },
    [],
  );

  const fetchReports = useCallback(async (filters?: ActivityReportFilters) => {
    try {
      setReportsLoading(true);
      const allReports: any[] = [];
      let page = 0;
      let hasNext = true;

      while (hasNext) {
        const response = await apiService.getActivityReportsList({
          ...filters,
          page,
          size: 1000,
        });
        allReports.push(...(response.items || []));
        hasNext = Boolean(response.hasNext);
        page += 1;
      }

      setReports(allReports);
      setReportsLoaded(true);
    } catch (listError) {
      try {
        const reportData = await apiService.getActivityReports({
          workflowContainerId: filters?.workflowContainerId,
          status: filters?.status,
          assigneeId: filters?.assigneeId,
          startDate: filters?.startDate,
          endDate: filters?.endDate,
        });
        setReports(applyLegacyExactReportFilters(reportData, filters));
        setReportsLoaded(true);
      } catch (error) {
        toast.error(tRef.current("errors:loadFailed"));
      }
    } finally {
      setReportsLoading(false);
    }
  }, [applyLegacyExactReportFilters]);

  useEffect(() => {
    void fetchProjects();
    void fetchUsers();
    if (canViewCosts) {
      void fetchCostRates();
    }
  }, [canViewCosts, fetchCostRates, fetchProjects, fetchUsers]);

  useEffect(() => {
    if (!canViewCosts) {
      setProjectCollections([]);
      return;
    }

    void fetchProjectCollections(
      projects
        .map((project: any) => Number(project?.id))
        .filter((id: number) => Number.isFinite(id) && id > 0),
    );
  }, [canViewCosts, fetchProjectCollections, projects]);

  useEffect(() => {
    if (user) {
      void fetchActivities();
    }
  }, [fetchActivities, user]);

  useEffect(() => {
    if (canViewCosts) {
      void fetchCostRates();
    } else {
      setUserHourlyCostRates([]);
      setCompanyAverageHourlyCostRates([]);
    }
  }, [canViewCosts, fetchCostRates]);

  const isLeaveActivityReport = useMemo(
    () => (report: any): boolean => {
      if (report?.isLeave === true) {
        return true;
      }

      const id = report.templateActivityId;
      if (typeof id === "number") {
        const def = activityDefinitions.find((item: any) => item.id === id);
        return def?.isLeave === true;
      }

      const title = (report.activityTitle || "").trim();
      if (!title) return false;
      const defByTitle = activityDefinitions.find(
        (item: any) => (item.title || "").trim() === title && item.isLeave === true,
      );
      return Boolean(defByTitle);
    },
    [activityDefinitions],
  );

  return {
    reports,
    setReports,
    projects,
    projectCollections,
    activityDefinitions,
    loading: reportsLoading,
    reportsLoading,
    projectsLoading,
    reportsLoaded,
    users,
    userHourlyCostRates,
    companyAverageHourlyCostRates,
    fetchReports,
    fetchProjects,
    fetchUsers,
    fetchActivities,
    fetchCostRates,
    isLeaveActivityReport,
  };
};
